import type {
	AccountSetupPayload,
	AiAgentCreatedPayload,
	AiAgentKnowledgePage,
	AiAgentKnowledgeSource,
	AiAgentKnowledgeSourceCreate,
	AiAgentRunPage,
	AiChatRatingReview,
	AiCopilotFeedbackInput,
	AiCopilotGuidance,
	AiCopilotQuestionInput,
	AiCopilotQualityReport,
	AiCopilotSuggestion,
	AiRoutingAnalysis,
	AiRoutingFeedbackInput,
	AiRoutingQualityReport,
	AiSkillAction,
	AiSkillActionInput,
	AiSkillActionTestInput,
	AiSkillActionTestResult,
	AiSkillConnection,
	AiSkillConnectionInput,
	AiSkillCredentialProfile,
	AiSkillCredentialProfileInput,
	AiSkillSettings,
	AiDraftSimulationRequest,
	AiDraftSimulationResult,
	AiEvaluationCase,
	AiEvaluationCaseInput,
	AiEvaluationComparison,
	AiKnowledgeCollection,
	AiKnowledgeCollectionCreate,
	AiKnowledgeCollectionUpdate,
	AgentReportPage,
	AgentReportRow,
	AgentReportSort,
	AuthenticatedUser,
	AuthenticationPayload,
	AuditEventPage,
	BlockedVisitorSummary,
	BlockVisitorResult,
	CannedResponseSummary,
	ChatIssueClassification,
	ChatIssueClassificationInput,
	ChatIssuesReport,
	ChatMessage,
	ConversationHistoryPage,
	ConversationStatus,
	ConversationSummary,
	ConversationTransferOptions,
	DashboardSummary,
	DepartmentRoutingSettings,
	DepartmentSummary,
	MembershipInvitationPayload,
	IntegrationSummary,
	PlatformAiSettings,
	PlatformAiSettingsUpdate,
	PlatformAiUsagePage,
	PlatformBrandingSettings,
	PlatformBrandingSettingsUpdate,
	PlatformAiAgentSettings,
	PlatformAiAgentSettingsUpdate,
	PlatformDashboardSummary,
	PlatformMessageEnhanceSettings,
	PlatformMessageEnhanceSettingsUpdate,
	PlatformTenantAiAgents,
	PlatformTenantAiAgentsUpdate,
	PlatformTenantAiCopilot,
	PlatformTenantAiCopilotUpdate,
	PlatformTenantAiChatRating,
	PlatformTenantAiChatRatingUpdate,
	PlatformTenantDetails,
	PlatformTenantMessageEnhance,
	PlatformTenantMessageEnhanceUpdate,
	PlatformTenantUpdate,
	ReportSortDirection,
	SoftwareUpdatePage,
	TenantCreatedPayload,
	TenantAiSettings,
	TenantAiSettingsUpdate,
	TenantMemberSummary,
	TenantMemberProfile,
	TenantMemberProfileUpdate,
	TenantMemberUpdate,
	TenantInvitation,
	TenantRole,
	TenantSummary,
	ThemePreference,
	VisitorPage,
	VisitorStatus,
	VisitorSummary,
	WebhookDeliverySummary,
	WebhookEndpointCreatedPayload,
	WebhookEndpointSummary,
	WebhookEventType,
} from '@livechat/shared';
import { createClientId } from '@livechat/shared';

export const apiUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:3000/v1';
export const realtimeUrl = apiUrl.replace(/\/v1\/?$/, '');

let accessToken: string | null = null;
let refreshPromise: Promise<AuthenticationPayload> | undefined;
const accessTokenListeners = new Set<(token: string | null) => void>();

export class ApiError extends Error {
	constructor(
		message: string,
		readonly status: number,
	) {
		super(message);
	}
}

async function request<T>(
	path: string,
	options: RequestInit = {},
	requireAuthentication = true,
	retry = true,
): Promise<T> {
	const headers = new Headers(options.headers);
	if (options.body && !(options.body instanceof FormData)) {
		headers.set('content-type', 'application/json');
	}
	if (requireAuthentication && accessToken) {
		headers.set('authorization', `Bearer ${accessToken}`);
	}

	const response = await fetch(`${apiUrl}${path}`, {
		...options,
		credentials: 'include',
		headers,
	});
	if (response.status === 401 && requireAuthentication && retry) {
		try {
			await refreshSession();
			return request<T>(path, options, requireAuthentication, false);
		} catch {
			setAccessToken(null);
		}
	}
	if (!response.ok) {
		const error = (await response.json().catch(() => null)) as {
			message?: string | string[];
		} | null;
		const message = Array.isArray(error?.message)
			? error.message.join(', ')
			: (error?.message ?? `Request failed with HTTP ${response.status}`);
		throw new ApiError(message, response.status);
	}
	if (response.status === 204) return undefined as T;
	return response.json() as Promise<T>;
}

function retainAuthentication(payload: AuthenticationPayload): AuthenticationPayload {
	setAccessToken(payload.accessToken);
	return payload;
}

function setAccessToken(token: string | null): void {
	accessToken = token;
	for (const listener of accessTokenListeners) listener(accessToken);
}

export function onAccessTokenChanged(listener: (token: string | null) => void): () => void {
	accessTokenListeners.add(listener);
	return () => accessTokenListeners.delete(listener);
}

export async function bootstrapStatus(): Promise<boolean> {
	const result = await request<{ required: boolean }>('/auth/bootstrap/status', {}, false);
	return result.required;
}

export async function bootstrap(input: {
	bootstrapToken: string;
	displayName: string;
	email: string;
	password: string;
}): Promise<AuthenticationPayload> {
	return retainAuthentication(
		await request<AuthenticationPayload>(
			'/auth/bootstrap',
			{ body: JSON.stringify(input), method: 'POST' },
			false,
		),
	);
}

export async function login(email: string, password: string): Promise<AuthenticationPayload> {
	return retainAuthentication(
		await request<AuthenticationPayload>(
			'/auth/login',
			{ body: JSON.stringify({ email, password }), method: 'POST' },
			false,
		),
	);
}

export function requestPasswordReset(
	email: string,
): Promise<{ emailConfigured: boolean; success: true }> {
	return request(
		'/auth/password-reset/request',
		{ body: JSON.stringify({ email }), method: 'POST' },
		false,
	);
}

export function completePasswordReset(input: {
	password: string;
	token: string;
}): Promise<{ success: true }> {
	return request(
		'/auth/password-reset/complete',
		{ body: JSON.stringify(input), method: 'POST' },
		false,
	);
}

export async function refreshSession(): Promise<AuthenticationPayload> {
	refreshPromise ??= request<AuthenticationPayload>('/auth/refresh', { method: 'POST' }, false)
		.then(retainAuthentication)
		.finally(() => {
			refreshPromise = undefined;
		});
	return refreshPromise;
}

export async function logout(): Promise<void> {
	await request('/auth/logout', { method: 'POST' }, false);
	setAccessToken(null);
}

export function listTenants(): Promise<TenantSummary[]> {
	return request('/platform/tenants');
}

export function getPlatformTenant(tenantId: string): Promise<PlatformTenantDetails> {
	return request(`/platform/tenants/${encodeURIComponent(tenantId)}`);
}

export function updateCustomerOpenRouter(
	tenantId: string,
	apiKey: string,
): Promise<{ apiKey: string }> {
	return request(`/platform/tenants/${encodeURIComponent(tenantId)}/openrouter`, {
		body: JSON.stringify({ apiKey }),
		method: 'PATCH',
	});
}

export function updatePlatformTenant(
	tenantId: string,
	input: PlatformTenantUpdate,
): Promise<PlatformTenantDetails> {
	return request(`/platform/tenants/${encodeURIComponent(tenantId)}`, {
		body: JSON.stringify(input),
		method: 'PATCH',
	});
}

export function updatePlatformTenantMessageEnhance(
	tenantId: string,
	input: PlatformTenantMessageEnhanceUpdate,
): Promise<PlatformTenantMessageEnhance> {
	return request(`/platform/tenants/${encodeURIComponent(tenantId)}/message-enhance`, {
		body: JSON.stringify(input),
		method: 'PATCH',
	});
}

export function updatePlatformTenantAiAgents(
	tenantId: string,
	input: PlatformTenantAiAgentsUpdate,
): Promise<PlatformTenantAiAgents> {
	return request(`/platform/tenants/${encodeURIComponent(tenantId)}/ai-agents`, {
		body: JSON.stringify(input),
		method: 'PATCH',
	});
}

export function updatePlatformTenantAiCopilot(
	tenantId: string,
	input: PlatformTenantAiCopilotUpdate,
): Promise<PlatformTenantAiCopilot> {
	return request(`/platform/tenants/${encodeURIComponent(tenantId)}/ai-copilot`, {
		body: JSON.stringify(input),
		method: 'PATCH',
	});
}

export function updatePlatformTenantAiChatRating(
	tenantId: string,
	input: PlatformTenantAiChatRatingUpdate,
): Promise<PlatformTenantAiChatRating> {
	return request(`/platform/tenants/${encodeURIComponent(tenantId)}/ai-chat-rating`, {
		body: JSON.stringify(input),
		method: 'PATCH',
	});
}

export function getPlatformAiSettings(): Promise<PlatformAiSettings> {
	return request('/platform/settings/ai');
}

export function updatePlatformAiSettings(
	input: PlatformAiSettingsUpdate,
): Promise<PlatformAiSettings> {
	return request('/platform/settings/ai', {
		body: JSON.stringify(input),
		method: 'PATCH',
	});
}

export function getPlatformBrandingSettings(): Promise<PlatformBrandingSettings> {
	return request('/platform/settings/branding');
}

export function updatePlatformBrandingSettings(
	input: PlatformBrandingSettingsUpdate,
): Promise<PlatformBrandingSettings> {
	return request('/platform/settings/branding', {
		body: JSON.stringify(input),
		method: 'PATCH',
	});
}

export function getPlatformMessageEnhanceSettings(): Promise<PlatformMessageEnhanceSettings> {
	return request('/platform/settings/message-enhance');
}

export function updatePlatformMessageEnhanceSettings(
	input: PlatformMessageEnhanceSettingsUpdate,
): Promise<PlatformMessageEnhanceSettings> {
	return request('/platform/settings/message-enhance', {
		body: JSON.stringify(input),
		method: 'PATCH',
	});
}

export function getPlatformDashboard(): Promise<PlatformDashboardSummary> {
	return request('/platform/dashboard');
}

export function getPlatformAiUsage(input: {
	action?: string;
	from: string;
	page: number;
	pageSize?: number;
	tenantId?: string;
	to: string;
}): Promise<PlatformAiUsagePage> {
	const parameters = new URLSearchParams({
		from: input.from,
		page: String(input.page),
		pageSize: String(input.pageSize ?? 25),
		to: input.to,
	});
	if (input.action) parameters.set('action', input.action);
	if (input.tenantId) parameters.set('tenantId', input.tenantId);
	return request(`/platform/ai-usage?${parameters}`);
}

export function getPlatformAiAgentSettings(): Promise<PlatformAiAgentSettings> {
	return request('/platform/settings/ai-agents');
}

export function updatePlatformAiAgentSettings(
	input: PlatformAiAgentSettingsUpdate,
): Promise<PlatformAiAgentSettings> {
	return request('/platform/settings/ai-agents', {
		body: JSON.stringify(input),
		method: 'PATCH',
	});
}

export function publishPlatformAiAgentSettings(): Promise<PlatformAiAgentSettings> {
	return request('/platform/settings/ai-agents/publish', { method: 'POST' });
}

export function restorePlatformAiAgentSettings(
	versionId: string,
): Promise<PlatformAiAgentSettings> {
	return request(
		`/platform/settings/ai-agents/versions/${encodeURIComponent(versionId)}/restore`,
		{ method: 'POST' },
	);
}

export function getTenantAiSettings(tenantId: string): Promise<TenantAiSettings> {
	return request(`/tenants/${encodeURIComponent(tenantId)}/ai-settings`);
}

export function updateTenantAiSettings(
	tenantId: string,
	input: TenantAiSettingsUpdate,
): Promise<TenantAiSettings> {
	return request(`/tenants/${encodeURIComponent(tenantId)}/ai-settings`, {
		body: JSON.stringify(input),
		method: 'PATCH',
	});
}

export function publishTenantAiConfiguration(tenantId: string): Promise<TenantAiSettings> {
	return request(`/tenants/${encodeURIComponent(tenantId)}/ai-settings/publish`, {
		method: 'POST',
	});
}

export function restoreTenantAiConfiguration(
	tenantId: string,
	versionId: string,
): Promise<TenantAiSettings> {
	return request(
		`/tenants/${encodeURIComponent(tenantId)}/ai-settings/versions/${encodeURIComponent(versionId)}/restore`,
		{ method: 'POST' },
	);
}

export function simulateTenantAiDraft(
	tenantId: string,
	input: AiDraftSimulationRequest,
): Promise<AiDraftSimulationResult> {
	return request(`/tenants/${encodeURIComponent(tenantId)}/ai-settings/simulations`, {
		body: JSON.stringify(input),
		method: 'POST',
	});
}

export function listTenantAiEvaluationCases(tenantId: string): Promise<AiEvaluationCase[]> {
	return request(`/tenants/${encodeURIComponent(tenantId)}/ai-settings/evaluations/cases`);
}

export function createTenantAiEvaluationCase(
	tenantId: string,
	input: AiEvaluationCaseInput,
): Promise<AiEvaluationCase> {
	return request(`/tenants/${encodeURIComponent(tenantId)}/ai-settings/evaluations/cases`, {
		body: JSON.stringify(input),
		method: 'POST',
	});
}

export function updateTenantAiEvaluationCase(
	tenantId: string,
	caseId: string,
	input: AiEvaluationCaseInput,
): Promise<AiEvaluationCase> {
	return request(
		`/tenants/${encodeURIComponent(tenantId)}/ai-settings/evaluations/cases/${encodeURIComponent(caseId)}`,
		{ body: JSON.stringify(input), method: 'PATCH' },
	);
}

export function deleteTenantAiEvaluationCase(
	tenantId: string,
	caseId: string,
): Promise<{ deleted: true }> {
	return request(
		`/tenants/${encodeURIComponent(tenantId)}/ai-settings/evaluations/cases/${encodeURIComponent(caseId)}`,
		{ method: 'DELETE' },
	);
}

export function runTenantAiEvaluationCase(
	tenantId: string,
	caseId: string,
): Promise<AiEvaluationComparison> {
	return request(
		`/tenants/${encodeURIComponent(tenantId)}/ai-settings/evaluations/cases/${encodeURIComponent(caseId)}/run`,
		{ method: 'POST' },
	);
}

export function resetTenantAiDraftToPublished(tenantId: string): Promise<TenantAiSettings> {
	return request(
		`/tenants/${encodeURIComponent(tenantId)}/ai-settings/draft/reset-to-published`,
		{ method: 'POST' },
	);
}

export function getTenantAiSkills(tenantId: string): Promise<AiSkillSettings> {
	return request(`/tenants/${encodeURIComponent(tenantId)}/ai/skills`);
}

export function setTenantAiSkillLiveExecution(
	tenantId: string,
	enabled: boolean,
): Promise<AiSkillSettings> {
	return request(`/tenants/${encodeURIComponent(tenantId)}/ai/skills/live-execution`, {
		method: 'POST',
		body: JSON.stringify({ enabled }),
	});
}

export function createTenantAiSkillVersion(
	tenantId: string,
	actionId: string,
): Promise<AiSkillSettings> {
	return request(
		`/tenants/${encodeURIComponent(tenantId)}/ai/skills/actions/${encodeURIComponent(actionId)}/versions`,
		{ method: 'POST' },
	);
}

export function saveTenantAiSkillAssignments(
	tenantId: string,
	assignments: AiSkillSettings['assignments'],
): Promise<AiSkillSettings> {
	return request(`/tenants/${encodeURIComponent(tenantId)}/ai/skills/assignments`, {
		method: 'POST',
		body: JSON.stringify({ assignments }),
	});
}

export function createTenantAiSkillConnection(
	tenantId: string,
	input: AiSkillConnectionInput,
): Promise<AiSkillConnection> {
	return request(`/tenants/${encodeURIComponent(tenantId)}/ai/skills/connections`, {
		body: JSON.stringify(input),
		method: 'POST',
	});
}

export function updateTenantAiSkillConnection(
	tenantId: string,
	connectionId: string,
	input: AiSkillConnectionInput & { status: 'active' | 'disabled' },
): Promise<AiSkillConnection> {
	return request(
		`/tenants/${encodeURIComponent(tenantId)}/ai/skills/connections/${encodeURIComponent(connectionId)}`,
		{ body: JSON.stringify(input), method: 'PATCH' },
	);
}

export function setTenantAiSkillConnectionStatus(
	tenantId: string,
	connectionId: string,
	status: AiSkillConnection['status'],
): Promise<AiSkillConnection> {
	return request(
		`/tenants/${encodeURIComponent(tenantId)}/ai/skills/connections/${encodeURIComponent(connectionId)}/status`,
		{ body: JSON.stringify({ status }), method: 'PATCH' },
	);
}

export function createTenantAiSkillCredential(
	tenantId: string,
	input: AiSkillCredentialProfileInput,
): Promise<AiSkillCredentialProfile> {
	return request(`/tenants/${encodeURIComponent(tenantId)}/ai/skills/credentials`, {
		body: JSON.stringify(input),
		method: 'POST',
	});
}

export function replaceTenantAiSkillCredential(
	tenantId: string,
	profileId: string,
	input:
		| { type: 'none' }
		| { secret: string; type: 'bearer' }
		| { headerName: string; secret: string; type: 'api_key_header' }
		| { password: string; type: 'basic'; username: string },
): Promise<AiSkillCredentialProfile> {
	return request(
		`/tenants/${encodeURIComponent(tenantId)}/ai/skills/credentials/${encodeURIComponent(profileId)}/replace`,
		{ body: JSON.stringify(input), method: 'POST' },
	);
}

export function setTenantAiSkillCredentialStatus(
	tenantId: string,
	profileId: string,
	status: AiSkillCredentialProfile['status'],
): Promise<AiSkillCredentialProfile> {
	return request(
		`/tenants/${encodeURIComponent(tenantId)}/ai/skills/credentials/${encodeURIComponent(profileId)}/status`,
		{ body: JSON.stringify({ status }), method: 'PATCH' },
	);
}

export function createTenantAiSkillAction(
	tenantId: string,
	input: AiSkillActionInput,
): Promise<AiSkillAction> {
	return request(`/tenants/${encodeURIComponent(tenantId)}/ai/skills/actions`, {
		body: JSON.stringify(input),
		method: 'POST',
	});
}

export function updateTenantAiSkillAction(
	tenantId: string,
	actionId: string,
	input: AiSkillActionInput,
): Promise<AiSkillAction> {
	return request(
		`/tenants/${encodeURIComponent(tenantId)}/ai/skills/actions/${encodeURIComponent(actionId)}`,
		{ body: JSON.stringify(input), method: 'PATCH' },
	);
}

export function setTenantAiSkillActionStatus(
	tenantId: string,
	actionId: string,
	status: AiSkillAction['status'],
): Promise<AiSkillAction> {
	return request(
		`/tenants/${encodeURIComponent(tenantId)}/ai/skills/actions/${encodeURIComponent(actionId)}/status`,
		{ body: JSON.stringify({ status }), method: 'PATCH' },
	);
}

export function testTenantAiSkillAction(
	tenantId: string,
	actionId: string,
	input: AiSkillActionTestInput,
): Promise<AiSkillActionTestResult> {
	return request(
		`/tenants/${encodeURIComponent(tenantId)}/ai/skills/actions/${encodeURIComponent(actionId)}/test`,
		{ body: JSON.stringify(input), method: 'POST' },
	);
}

export function createTenant(input: {
	name: string;
	ownerDisplayName: string;
	ownerEmail: string;
	planCode: string;
}): Promise<TenantCreatedPayload> {
	return request('/platform/tenants', {
		body: JSON.stringify(input),
		method: 'POST',
	});
}

export function updateTenantStatus(
	tenantId: string,
	status: TenantSummary['status'],
): Promise<TenantSummary> {
	return request(`/platform/tenants/${tenantId}/status`, {
		body: JSON.stringify({ status }),
		method: 'PATCH',
	});
}

export function listMembers(tenantId: string): Promise<TenantMemberSummary[]> {
	return request(`/tenants/${tenantId}/members`);
}

export function inviteMember(
	tenantId: string,
	input: {
		displayName: string;
		email: string;
		role: TenantRole;
	},
): Promise<TenantInvitation> {
	return request(`/tenants/${tenantId}/members/invitations`, {
		body: JSON.stringify(input),
		method: 'POST',
	});
}

export function createAiAgent(
	tenantId: string,
	input: { displayName: string; email: string },
): Promise<AiAgentCreatedPayload> {
	return request(`/tenants/${tenantId}/members/ai-agents`, {
		body: JSON.stringify(input),
		method: 'POST',
	});
}

export function listAiKnowledgeCollections(tenantId: string): Promise<AiKnowledgeCollection[]> {
	return request(`/tenants/${tenantId}/knowledge-collections`);
}

export function createAiKnowledgeCollection(
	tenantId: string,
	input: AiKnowledgeCollectionCreate,
): Promise<AiKnowledgeCollection> {
	return request(`/tenants/${tenantId}/knowledge-collections`, {
		body: JSON.stringify(input),
		method: 'POST',
	});
}

export function updateAiKnowledgeCollection(
	tenantId: string,
	collectionId: string,
	input: AiKnowledgeCollectionUpdate,
): Promise<AiKnowledgeCollection> {
	return request(`/tenants/${tenantId}/knowledge-collections/${collectionId}`, {
		body: JSON.stringify(input),
		method: 'PATCH',
	});
}

export function deleteAiKnowledgeCollection(tenantId: string, collectionId: string): Promise<void> {
	return request(`/tenants/${tenantId}/knowledge-collections/${collectionId}`, {
		method: 'DELETE',
	});
}

export function listAiKnowledgeSources(
	tenantId: string,
	collectionId: string,
	input: { page: number; pageSize: number },
): Promise<AiAgentKnowledgePage> {
	const query = new URLSearchParams({
		page: String(input.page),
		pageSize: String(input.pageSize),
	});
	return request(`/tenants/${tenantId}/knowledge-collections/${collectionId}/sources?${query}`);
}

export function listAiAgentRuns(
	tenantId: string,
	input: { page: number; pageSize: number },
): Promise<AiAgentRunPage> {
	const query = new URLSearchParams({
		page: String(input.page),
		pageSize: String(input.pageSize),
	});
	return request(`/tenants/${tenantId}/ai-runs?${query}`);
}

export function createAiKnowledgeSource(
	tenantId: string,
	collectionId: string,
	input: AiAgentKnowledgeSourceCreate,
): Promise<AiAgentKnowledgeSource> {
	return request(`/tenants/${tenantId}/knowledge-collections/${collectionId}/sources`, {
		body: JSON.stringify(input),
		method: 'POST',
	});
}

export function uploadAiKnowledgeDocument(
	tenantId: string,
	collectionId: string,
	input: { file: File; title: string },
): Promise<AiAgentKnowledgeSource> {
	const body = new FormData();
	body.append('file', input.file);
	return request(
		`/tenants/${tenantId}/knowledge-collections/${collectionId}/sources/documents?title=${encodeURIComponent(input.title)}`,
		{ body, method: 'POST' },
	);
}

export function deleteAiKnowledgeSource(
	tenantId: string,
	collectionId: string,
	sourceId: string,
): Promise<void> {
	return request(
		`/tenants/${tenantId}/knowledge-collections/${collectionId}/sources/${sourceId}`,
		{
			method: 'DELETE',
		},
	);
}

export function refreshAiKnowledgeSource(
	tenantId: string,
	collectionId: string,
	sourceId: string,
): Promise<AiAgentKnowledgeSource> {
	return request(
		`/tenants/${tenantId}/knowledge-collections/${collectionId}/sources/${sourceId}/refresh`,
		{ method: 'POST' },
	);
}

export function updateMember(
	tenantId: string,
	userId: string,
	input: TenantMemberUpdate,
): Promise<TenantMemberSummary> {
	return request(`/tenants/${tenantId}/members/${userId}`, {
		body: JSON.stringify(input),
		method: 'PATCH',
	});
}

export function deleteMember(tenantId: string, userId: string): Promise<void> {
	return request(`/tenants/${tenantId}/members/${userId}`, { method: 'DELETE' });
}

export function getMemberProfile(tenantId: string, userId: string): Promise<TenantMemberProfile> {
	return request(`/tenants/${tenantId}/members/${userId}/profile`);
}

export function completeMemberInvitation(
	tenantId: string,
	userId: string,
	password: string,
): Promise<TenantMemberProfile> {
	return request(`/tenants/${tenantId}/members/${userId}/complete-invitation`, {
		body: JSON.stringify({ password }),
		method: 'POST',
	});
}

export function updateMemberProfile(
	tenantId: string,
	userId: string,
	input: TenantMemberProfileUpdate,
): Promise<TenantMemberProfile> {
	return request(`/tenants/${tenantId}/members/${userId}/profile`, {
		body: JSON.stringify(input),
		method: 'PATCH',
	});
}

export function previewMembershipInvitation(token: string): Promise<MembershipInvitationPayload> {
	return request(`/auth/membership-invitations?token=${encodeURIComponent(token)}`, {}, false);
}

export function acceptMembershipInvitation(token: string): Promise<{ tenantId: string }> {
	return request('/auth/membership-invitations/accept', {
		body: JSON.stringify({ token }),
		method: 'POST',
	});
}

export function previewAccountSetup(token: string): Promise<AccountSetupPayload> {
	return request(`/auth/account-setup?token=${encodeURIComponent(token)}`, {}, false);
}

export function completeAccountSetup(input: {
	displayName: string;
	password: string;
	token: string;
}): Promise<{ success: true }> {
	return request('/auth/account-setup', { body: JSON.stringify(input), method: 'POST' }, false);
}

export function getIntegration(tenantId: string): Promise<IntegrationSummary> {
	return request(`/tenants/${tenantId}/integration`);
}

export function updateIntegration(
	tenantId: string,
	input: Partial<
		Pick<
			IntegrationSummary,
			'allowedDomains' | 'operationalSettings' | 'status' | 'widgetSettings'
		>
	>,
): Promise<IntegrationSummary> {
	return request(`/tenants/${tenantId}/integration`, {
		body: JSON.stringify(input),
		method: 'PATCH',
	});
}

export function deleteChatAndVisitorData(
	tenantId: string,
): Promise<{ conversationsDeleted: number; visitorsDeleted: number }> {
	return request(`/tenants/${tenantId}/integration/data`, { method: 'DELETE' });
}

export function listDepartments(tenantId: string): Promise<DepartmentSummary[]> {
	return request(`/tenants/${tenantId}/departments`);
}

export function reorderDepartments(
	tenantId: string,
	departmentIds: string[],
): Promise<DepartmentSummary[]> {
	return request(`/tenants/${tenantId}/departments/order`, {
		body: JSON.stringify({ departmentIds }),
		method: 'PUT',
	});
}

export function createDepartment(
	tenantId: string,
	input: { name: string; routingSettings: DepartmentRoutingSettings },
): Promise<DepartmentSummary> {
	return request(`/tenants/${tenantId}/departments`, {
		body: JSON.stringify(input),
		method: 'POST',
	});
}

export function updateDepartment(
	tenantId: string,
	departmentId: string,
	input: Partial<Pick<DepartmentSummary, 'name' | 'routingSettings' | 'status'>>,
): Promise<DepartmentSummary> {
	return request(`/tenants/${tenantId}/departments/${departmentId}`, {
		body: JSON.stringify(input),
		method: 'PATCH',
	});
}

export function deleteDepartment(tenantId: string, departmentId: string): Promise<void> {
	return request(`/tenants/${tenantId}/departments/${departmentId}`, {
		method: 'DELETE',
	});
}

export function setDepartmentMembers(
	tenantId: string,
	departmentId: string,
	members: { priority: number; userId: string }[],
): Promise<DepartmentSummary> {
	return request(`/tenants/${tenantId}/departments/${departmentId}/members`, {
		body: JSON.stringify({ members }),
		method: 'PUT',
	});
}

export function listConversations(
	tenantId: string,
	status: ConversationStatus | 'all' = 'all',
): Promise<ConversationSummary[]> {
	return request(`/tenants/${tenantId}/conversations?status=${status}`);
}

export function getDashboard(
	tenantId: string,
	input: { from: string; timezone: string; to: string },
): Promise<DashboardSummary> {
	const parameters = new URLSearchParams({
		from: input.from,
		timezone: input.timezone,
		to: input.to,
	});
	return request(`/tenants/${tenantId}/dashboard?${parameters}`);
}

interface AgentReportRangeInput {
	from: string;
	fromTime: string;
	order: ReportSortDirection;
	sort: AgentReportSort;
	timezone: string;
	to: string;
	toTime: string;
}

function agentReportParameters(input: AgentReportRangeInput): URLSearchParams {
	return new URLSearchParams({
		from: input.from,
		fromTime: input.fromTime,
		order: input.order,
		sort: input.sort,
		timezone: input.timezone,
		to: input.to,
		toTime: input.toTime,
	});
}

export function getAgentReport(
	tenantId: string,
	input: AgentReportRangeInput & { page: number; pageSize?: number },
): Promise<AgentReportPage> {
	const parameters = agentReportParameters(input);
	parameters.set('page', String(input.page));
	parameters.set('pageSize', String(input.pageSize ?? 25));
	return request(`/tenants/${tenantId}/reports/agents?${parameters}`);
}

export function getAgentReportExport(
	tenantId: string,
	input: AgentReportRangeInput,
): Promise<AgentReportRow[]> {
	return request(`/tenants/${tenantId}/reports/agents/export?${agentReportParameters(input)}`);
}

export function getChatIssuesReport(
	tenantId: string,
	input: { date: string; timezone: string },
): Promise<ChatIssuesReport> {
	return request(`/tenants/${tenantId}/reports/chat-issues?${new URLSearchParams(input)}`);
}

export function createChatIssueClassification(
	tenantId: string,
	input: ChatIssueClassificationInput,
): Promise<ChatIssueClassification> {
	return request(`/tenants/${tenantId}/reports/chat-issues/classifications`, {
		body: JSON.stringify(input),
		method: 'POST',
	});
}

export function deleteChatIssueClassification(
	tenantId: string,
	classificationId: string,
): Promise<{ deleted: true }> {
	return request(`/tenants/${tenantId}/reports/chat-issues/classifications/${classificationId}`, {
		method: 'DELETE',
	});
}
export function getAiCopilotQualityReport(
	tenantId: string,
	input: Pick<AgentReportRangeInput, 'from' | 'fromTime' | 'timezone' | 'to' | 'toTime'>,
): Promise<AiCopilotQualityReport> {
	return request(
		`/tenants/${tenantId}/reports/ai-copilot?${new URLSearchParams({
			from: input.from,
			fromTime: input.fromTime,
			timezone: input.timezone,
			to: input.to,
			toTime: input.toTime,
		})}`,
	);
}

export function getAiRoutingQualityReport(
	tenantId: string,
	input: Pick<AgentReportRangeInput, 'from' | 'fromTime' | 'timezone' | 'to' | 'toTime'>,
): Promise<AiRoutingQualityReport> {
	return request(
		`/tenants/${tenantId}/reports/ai-routing?${new URLSearchParams({
			from: input.from,
			fromTime: input.fromTime,
			timezone: input.timezone,
			to: input.to,
			toTime: input.toTime,
		})}`,
	);
}

export function listConversationMessages(
	tenantId: string,
	conversationId: string,
): Promise<ChatMessage[]> {
	return request(`/tenants/${tenantId}/conversations/${conversationId}/messages`);
}

export function getConversation(
	tenantId: string,
	conversationId: string,
): Promise<ConversationSummary> {
	return request(`/tenants/${tenantId}/conversations/${conversationId}`);
}

export function getConversationAiRatingReview(
	tenantId: string,
	conversationId: string,
): Promise<AiChatRatingReview | null> {
	return request(`/tenants/${tenantId}/conversations/${conversationId}/ai-review`);
}

export function listConversationHistory(
	tenantId: string,
	input: {
		assignedUserId?: string;
		classification?: string;
		page: number;
		pageSize?: number;
		query?: string;
		visitorId?: string;
	},
): Promise<ConversationHistoryPage> {
	const parameters = new URLSearchParams({
		page: String(input.page),
		pageSize: String(input.pageSize ?? 25),
	});
	if (input.assignedUserId) parameters.set('assignedUserId', input.assignedUserId);
	if (input.classification) parameters.set('classification', input.classification);
	if (input.query) parameters.set('query', input.query);
	if (input.visitorId) parameters.set('visitorId', input.visitorId);
	return request(`/tenants/${tenantId}/conversations/history?${parameters}`);
}

export function listVisitors(
	tenantId: string,
	input: {
		from?: string;
		order?: 'asc' | 'desc';
		page: number;
		pageSize?: number;
		query?: string;
		sort?: 'visitor' | 'device' | 'location' | 'page' | 'time-online' | 'visits' | 'chats';
		status?: VisitorStatus | 'all' | 'online';
		to?: string;
	},
): Promise<VisitorPage> {
	const parameters = new URLSearchParams({
		page: String(input.page),
		pageSize: String(input.pageSize ?? 50),
		status: input.status ?? 'all',
	});
	if (input.order) parameters.set('order', input.order);
	if (input.from) parameters.set('from', input.from);
	if (input.query) parameters.set('query', input.query);
	if (input.sort) parameters.set('sort', input.sort);
	if (input.to) parameters.set('to', input.to);
	return request(`/tenants/${tenantId}/visitors?${parameters}`);
}

export function getVisitor(tenantId: string, visitorId: string): Promise<VisitorSummary> {
	return request(`/tenants/${tenantId}/visitors/${visitorId}`);
}

export function startVisitorConversation(
	tenantId: string,
	visitorId: string,
): Promise<ConversationSummary> {
	return request(`/tenants/${tenantId}/visitors/${visitorId}/conversations`, {
		body: JSON.stringify({}),
		method: 'POST',
	});
}

export function sendConversationMessage(
	tenantId: string,
	conversationId: string,
	body: string,
	whisper = false,
): Promise<ChatMessage> {
	return request(`/tenants/${tenantId}/conversations/${conversationId}/messages`, {
		body: JSON.stringify({ body, clientId: createClientId(), whisper }),
		method: 'POST',
	});
}

export function blockConversationVisitor(
	tenantId: string,
	conversationId: string,
): Promise<BlockVisitorResult> {
	return request(`/tenants/${tenantId}/conversations/${conversationId}/block`, {
		method: 'POST',
	});
}

export function emailConversationTranscript(
	tenantId: string,
	conversationId: string,
	email: string,
): Promise<{ emailDelivery: 'queued' }> {
	return request(`/tenants/${tenantId}/conversations/${conversationId}/transcript`, {
		body: JSON.stringify({ email }),
		method: 'POST',
	});
}

export function pushConversationVisitor(
	tenantId: string,
	conversationId: string,
	url: string,
): Promise<{ pushed: true }> {
	return request(`/tenants/${tenantId}/conversations/${conversationId}/push`, {
		body: JSON.stringify({ url }),
		method: 'POST',
	});
}

export function listBlockedVisitors(tenantId: string): Promise<BlockedVisitorSummary[]> {
	return request(`/tenants/${tenantId}/blocked-visitors`);
}

export function unblockVisitor(tenantId: string, blockedVisitorId: string): Promise<void> {
	return request(`/tenants/${tenantId}/blocked-visitors/${blockedVisitorId}`, {
		method: 'DELETE',
	});
}

export function suggestConversationReply(
	tenantId: string,
	conversationId: string,
): Promise<AiCopilotSuggestion> {
	return request(`/tenants/${tenantId}/conversations/${conversationId}/copilot/suggestions`, {
		method: 'POST',
	});
}

export function askConversationAi(
	tenantId: string,
	conversationId: string,
	input: AiCopilotQuestionInput,
): Promise<AiCopilotGuidance> {
	return request(`/tenants/${tenantId}/conversations/${conversationId}/copilot/ask`, {
		body: JSON.stringify(input),
		method: 'POST',
	});
}

export function helpWithConversationAnswer(
	tenantId: string,
	conversationId: string,
): Promise<AiCopilotGuidance> {
	return request(`/tenants/${tenantId}/conversations/${conversationId}/copilot/help`, {
		method: 'POST',
	});
}

export function summarizeConversation(
	tenantId: string,
	conversationId: string,
): Promise<AiCopilotGuidance> {
	return request(`/tenants/${tenantId}/conversations/${conversationId}/copilot/summary`, {
		method: 'POST',
	});
}

export function analyseConversationRouting(
	tenantId: string,
	conversationId: string,
): Promise<AiRoutingAnalysis> {
	return request(`/tenants/${tenantId}/conversations/${conversationId}/routing/analyse`, {
		method: 'POST',
	});
}

export function getLatestConversationRoutingAnalysis(
	tenantId: string,
	conversationId: string,
): Promise<AiRoutingAnalysis | null> {
	return request(`/tenants/${tenantId}/conversations/${conversationId}/routing/latest`);
}

export function recordConversationRoutingFeedback(
	tenantId: string,
	conversationId: string,
	analysisId: string,
	input: AiRoutingFeedbackInput,
): Promise<void> {
	return request(
		`/tenants/${tenantId}/conversations/${conversationId}/routing/analyses/${analysisId}/feedback`,
		{ body: JSON.stringify(input), method: 'POST' },
	);
}

export function recordConversationCopilotFeedback(
	tenantId: string,
	conversationId: string,
	runId: string,
	input: AiCopilotFeedbackInput,
): Promise<void> {
	return request(
		`/tenants/${tenantId}/conversations/${conversationId}/copilot/runs/${runId}/feedback`,
		{ body: JSON.stringify(input), method: 'POST' },
	);
}

export function assignConversation(
	tenantId: string,
	conversationId: string,
	userId?: string,
): Promise<ConversationSummary> {
	return request(`/tenants/${tenantId}/conversations/${conversationId}/assign`, {
		body: JSON.stringify(userId ? { userId } : {}),
		method: 'POST',
	});
}

export function getConversationTransferOptions(
	tenantId: string,
	conversationId: string,
): Promise<ConversationTransferOptions> {
	return request(`/tenants/${tenantId}/conversations/${conversationId}/transfer-options`);
}

export function transferConversation(
	tenantId: string,
	conversationId: string,
	input: { departmentId: string } | { userId: string },
): Promise<ConversationSummary> {
	return request(`/tenants/${tenantId}/conversations/${conversationId}/transfer`, {
		body: JSON.stringify(input),
		method: 'POST',
	});
}

export function updateConversation(
	tenantId: string,
	conversationId: string,
	input: {
		priority?: ConversationSummary['priority'];
		status?: ConversationStatus;
	},
): Promise<ConversationSummary> {
	return request(`/tenants/${tenantId}/conversations/${conversationId}`, {
		body: JSON.stringify(input),
		method: 'PATCH',
	});
}

export function closeConversation(
	tenantId: string,
	conversationId: string,
): Promise<ConversationSummary> {
	return request(`/tenants/${tenantId}/conversations/${conversationId}/close`, {
		method: 'POST',
	});
}

export function currentAccessToken(): string | null {
	return accessToken;
}

export function updateThemePreference(
	themePreference: ThemePreference,
): Promise<AuthenticatedUser> {
	return request('/auth/preferences', {
		body: JSON.stringify({ themePreference }),
		method: 'PATCH',
	});
}

export function changePassword(input: {
	currentPassword: string;
	password: string;
}): Promise<{ success: true }> {
	return request('/auth/password', {
		body: JSON.stringify(input),
		method: 'PATCH',
	});
}

export function listCannedResponses(tenantId: string): Promise<CannedResponseSummary[]> {
	return request(`/tenants/${tenantId}/canned-responses`);
}

export function createCannedResponse(
	tenantId: string,
	input: {
		body: string;
		name: string;
		scope: CannedResponseSummary['scope'];
		trigger?: string;
	},
): Promise<CannedResponseSummary> {
	return request(`/tenants/${tenantId}/canned-responses`, {
		body: JSON.stringify(input),
		method: 'POST',
	});
}

export function updateCannedResponse(
	tenantId: string,
	responseId: string,
	input: Partial<Pick<CannedResponseSummary, 'body' | 'name' | 'status'>> & {
		trigger?: string | null;
	},
): Promise<CannedResponseSummary> {
	return request(`/tenants/${tenantId}/canned-responses/${responseId}`, {
		body: JSON.stringify(input),
		method: 'PATCH',
	});
}

export function deleteCannedResponse(tenantId: string, responseId: string): Promise<void> {
	return request(`/tenants/${tenantId}/canned-responses/${responseId}`, {
		method: 'DELETE',
	});
}

export function listWebhooks(tenantId: string): Promise<WebhookEndpointSummary[]> {
	return request(`/tenants/${tenantId}/webhooks`);
}

export function createWebhook(
	tenantId: string,
	input: { description: string; eventTypes: WebhookEventType[]; url: string },
): Promise<WebhookEndpointCreatedPayload> {
	return request(`/tenants/${tenantId}/webhooks`, {
		body: JSON.stringify(input),
		method: 'POST',
	});
}

export function updateWebhook(
	tenantId: string,
	endpointId: string,
	input: Partial<Pick<WebhookEndpointSummary, 'description' | 'eventTypes' | 'status' | 'url'>>,
): Promise<WebhookEndpointSummary> {
	return request(`/tenants/${tenantId}/webhooks/${endpointId}`, {
		body: JSON.stringify(input),
		method: 'PATCH',
	});
}

export function rotateWebhookSecret(
	tenantId: string,
	endpointId: string,
): Promise<{ signingSecret: string }> {
	return request(`/tenants/${tenantId}/webhooks/${endpointId}/rotate-secret`, {
		method: 'POST',
	});
}

export function listWebhookDeliveries(
	tenantId: string,
	endpointId: string,
): Promise<WebhookDeliverySummary[]> {
	return request(`/tenants/${tenantId}/webhooks/${endpointId}/deliveries`);
}

export function listAuditEvents(
	tenantId: string,
	input: { page: number; pageSize?: number },
): Promise<AuditEventPage> {
	const parameters = new URLSearchParams({
		page: String(input.page),
		pageSize: String(input.pageSize ?? 25),
	});
	return request(`/tenants/${tenantId}/audit-events?${parameters}`);
}

export function listSoftwareUpdates(
	tenantId: string,
	input: { page: number; pageSize?: number },
): Promise<SoftwareUpdatePage> {
	const parameters = new URLSearchParams({
		page: String(input.page),
		pageSize: String(input.pageSize ?? 25),
	});
	return request(`/tenants/${tenantId}/updates?${parameters}`);
}

export type { AuthenticatedUser };
